'use client';

import { useState, useEffect } from 'react';
import { safeReturn } from '@/lib/assistant/return.mjs';
import { consumeSessionExpiryNotice } from '@/lib/session-expiry.mjs';
import dynamic from 'next/dynamic';

const LoginHeroFilm = dynamic(() => import('./hero-film'), { ssr: false });

function LogoMark() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
      <span role="img" aria-label="Pearson & Partners" style={{ display: 'inline-block', width: 30, height: 20, backgroundColor: '#000000', WebkitMask: 'url(/logo-pmark-white.png) center/contain no-repeat', mask: 'url(/logo-pmark-white.png) center/contain no-repeat' }} />
    </div>
  );
}

// Login icon: door with arrow pointing right (inline stroke SVG, no emoji).
const LoginIcon = (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
    <polyline points="10 17 15 12 10 7" />
    <line x1="15" y1="12" x2="3" y2="12" />
  </svg>
);

// External-link icon: box with outbound arrow (inline stroke SVG).
const ExternalIcon = (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const SunIcon = (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
  </svg>
);

const MoonIcon = (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

// Grove Amsterdamse nachtcheck voor de begintoestand van de dag/nacht-knop;
// de shader zelf hanteert de fijne overgangsrampen (hero-film.js).
function amsterdamNight() {
  try {
    const h = +new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Amsterdam', hour: 'numeric', hour12: false }).format(new Date());
    return h >= 20 || h < 7;
  } catch { return false; }
}

// EU flag as inline SVG (no emoji, no external image): blue field, 12 gold stars.
const EuFlag = (
  <svg viewBox="0 0 30 20" width="21" height="14" aria-hidden="true" style={{ flexShrink: 0, display: 'block' }}>
    <rect width="30" height="20" fill="#003399" />
    <g fill="#ffcc00">
      {Array.from({ length: 12 }, (_, i) => {
        const a = (i * 30 - 90) * Math.PI / 180;
        // toFixed: identieke stringserialisatie op server en client (hydration).
        const cx = (15 + Math.cos(a) * 6.5).toFixed(3);
        const cy = (10 + Math.sin(a) * 6.5).toFixed(3);
        return <path key={i} transform={`translate(${cx} ${cy}) scale(1.6)`} d="M0,-1 L0.588,0.809 L-0.951,-0.309 L0.951,-0.309 L-0.588,0.809 Z" />;
      })}
    </g>
  </svg>
);

const inputStyle = { width: '100%', background: '#ffffff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '10px 14px', fontFamily: 'inherit', fontSize: 13 /* = HV2.fsText */, color: '#000000', outline: 'none', boxSizing: 'border-box' };
// Knop-diepte: universele knop-diepte, zie BTN_RELIEF in components/HomeV2/lib.js (deze pre-auth pagina importeert HomeV2 bewust niet).
const btnStyle = {
  width: '100%', padding: '10px 18px',
  background: '#ffffff', color: '#000000',
  borderRadius: 5,
  border: '1px solid rgba(0,0,0,0.24)',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.4), inset 0 -1px 2px rgba(0,0,0,0.12), 0 1px 1px rgba(0,0,0,0.22)',
  fontFamily: 'inherit', fontSize: 13 /* = HV2.fsText */, fontWeight: 560,
  cursor: 'pointer', letterSpacing: '0.3px',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  transition: 'box-shadow 0.08s, transform 0.08s',
};
const btnPressedStyle = {
  boxShadow: 'inset 0 1.5px 3px rgba(0,0,0,0.26), inset 0 -1px 0 rgba(255,255,255,0.24)',
  transform: 'translateY(0.5px)',
};

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    const notice = consumeSessionExpiryNotice(window);
    if (notice) setError(notice);
  }, []);
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState('password');
  const [code, setCode] = useState('');
  const [pressed, setPressed] = useState(false);
  // null = Amsterdamse klok; 0/1 = door de gebruiker geforceerde dag/nacht.
  const [nightOverride, setNightOverride] = useState(null);
  const [autoNight] = useState(() => amsterdamNight());
  const showingNight = nightOverride !== null ? nightOverride === 1 : autoNight;

  const finish = () => {
    consumeSessionExpiryNotice(window);
    const next = safeReturn(new URLSearchParams(window.location.search).get('next'));
    window.location.replace(next === '/' ? '/' + (window.location.hash || '') : next);
  };

  const checkWeekend = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/auth/weekend-status');
      const data = await res.json();
      if (data.ok) finish();
      else if (data.status === 'pending') setError('Goedkeuring is nog niet gegeven.');
      else setError(data.error || 'Toegang is niet goedgekeurd.');
    } catch { setError('Verbindingsfout'); }
    setLoading(false);
  };

  const verifyTwoFactor = async () => {
    if (!code.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/auth/2fa/verify', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code: code.trim() }),
      });
      const data = await res.json();
      if (data.ok) finish();
      else if (data.needsWeekendApproval) setStage('weekend');
      // Het pending-token is weg (te lang gewacht, ander netwerk, elders ingelogd). Zonder
      // deze stap bleef de gebruiker achter op een codescherm dat nooit meer kon slagen.
      else if (data.restart) { setStage('password'); setCode(''); setPassword(''); setError(data.error || 'Log opnieuw in.'); }
      else setError(data.error || 'Controle mislukt');
    } catch { setError('Verbindingsfout'); }
    setLoading(false);
  };

  const handleLogin = async () => {
    if (!email.trim() || !password) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json();
      if (data.ok) {
        // Preserve deep-link fragments (#calls, #crm-…) across the login hop.
        // Directors landen ook gewoon in de app (eigenaarsbesluit 2026-07-10);
        // /director bereiken ze zelf via het menu wanneer nodig.
        finish();
      } else if (data.needsEnrollment) {
        window.location.href = '/2fa-setup?next=' + encodeURIComponent(safeReturn(new URLSearchParams(window.location.search).get('next')));
      } else if (data.needs2fa) {
        setStage('2fa');
      } else if (data.needsWeekendApproval) {
        setStage('weekend');
      } else {
        setError(data.error || 'Inloggen mislukt');
      }
    } catch {
      setError('Verbindingsfout');
    }
    setLoading(false);
  };

  // Uitgeschakelde knop moet ook visueel dood ogen: gedimd zwart oogt op deze kaart
  // als een gewoon grijs knopje, en cursor bleef pointer (7 augustus 2026). Vlak
  // (geen reliëf, want reliëf betekent drukbaar in dit systeem), een bewust
  // gedempte vulling in plaats van de opacity truc, en cursor not-allowed. Eenmaal
  // berekend zodat disabled={} en de stijl nooit uit elkaar kunnen lopen.
  const disabled = (stage === 'password' && (!email.trim() || !password)) || (stage === '2fa' && !code.trim()) || loading;

  const currentBtnStyle = disabled
    ? { ...btnStyle, cursor: 'not-allowed', boxShadow: 'none', background: '#e8e8e8', color: '#999999', transition: 'none' }
    : pressed
      ? { ...btnStyle, ...btnPressedStyle }
      : btnStyle;

  return (
    <>
      <LoginHeroFilm nightOverride={nightOverride} />
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit', padding: '20px', position: 'relative', zIndex: 1 }}>
        <div style={{
          background: '#ffffff', border: '1px solid rgba(0,0,0,0.08)',
          borderRadius: '4px', width: '100%', maxWidth: '420px', padding: '36px',
          position: 'relative', overflow: 'hidden',
        }}>
          {/* Eigenaarsverzoek: drie gelijke vlakken (oranje, zalm, roze) bovenop het loginvenster. */}
          <div aria-hidden style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 10, display: 'flex' }}>
            <div style={{ flex: 1, background: '#e8794a' }} />
            <div style={{ flex: 1, background: '#f0a795' }} />
            <div style={{ flex: 1, background: '#f3c2d3' }} />
          </div>
          <button data-knop
            onClick={() => setNightOverride(showingNight ? 0 : 1)}
            aria-label={showingNight ? 'Schakel naar dag' : 'Schakel naar nacht'}
            title={showingNight ? 'Dag' : 'Nacht'}
            style={{
              position: 'absolute', right: 10, bottom: 10,
              width: 26, height: 26, padding: 0,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: 'transparent', border: 'none', borderRadius: 5,
              color: '#c0c0c0', cursor: 'pointer',
            }}>
            {showingNight ? SunIcon : MoonIcon}
          </button>
          <div style={{ marginBottom: '24px' }}><LogoMark /></div>
          <div style={{ fontSize: 13 /* = HV2.fsText */, fontWeight: 560, color: '#000000', marginBottom: '4px' }}>Inloggen</div>
          <div style={{ fontSize: 13 /* = HV2.fsText */, color: '#888888', marginBottom: '20px' }}>
            {stage === '2fa' ? 'Voer je verificatiecode of herstelcode in' : stage === 'weekend' ? 'Toegang wacht op goedkeuring' : 'Log in met je e-mailadres en wachtwoord'}
          </div>

          {stage === 'password' && <><div style={{ marginBottom: '12px' }}>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleLogin()}
              placeholder="E-mailadres"
              autoComplete="email"
              style={{ ...inputStyle, borderColor: error ? '#dc2626' : '#e0e0e0' }}
            />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleLogin()}
              placeholder="Wachtwoord"
              autoComplete="current-password"
              style={{ ...inputStyle, borderColor: error ? '#dc2626' : '#e0e0e0' }}
            />
          </div></>}

          {stage === '2fa' && <div style={{ marginBottom: '16px' }}>
            <input value={code} onChange={e => setCode(e.target.value)} onKeyDown={e => e.key === 'Enter' && verifyTwoFactor()}
              placeholder="000000 of herstelcode" autoComplete="one-time-code" inputMode="numeric"
              style={{ ...inputStyle, borderColor: error ? '#dc2626' : '#e0e0e0' }} autoFocus />
          </div>}

          {error && <div style={{ fontSize: 13 /* = HV2.fsText */, color: '#dc2626', marginBottom: '12px' }}>{error}</div>}

          <button onClick={stage === '2fa' ? verifyTwoFactor : stage === 'weekend' ? checkWeekend : handleLogin}
            disabled={disabled}
            onMouseDown={() => !disabled && setPressed(true)}
            onMouseUp={() => setPressed(false)}
            onMouseLeave={() => setPressed(false)}
            style={currentBtnStyle}>
            {loading ? 'Controleren...' : <>{stage === '2fa' ? 'Verifiëren' : stage === 'weekend' ? 'Status controleren' : 'Inloggen'}{!loading && LoginIcon}</>}
          </button>

          <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #eeeeee', textAlign: 'center' }}>
            <a className="pb-tl" href="https://www.pearsonpartners.nl" target="_blank" rel="noopener noreferrer"
              style={{ fontSize: 13 /* = HV2.fsText */, color: '#2d4f9e', textDecoration: 'none' }}>
              Information for employers →
            </a>
            {/* Vlakke knop (geen reliëf): navigatie naar extern privacybeleid, geen actie. */}
            <div style={{ marginTop: '12px' }}>
            <a data-knop href="https://pearsonpartners.nl/privacy/" target="_blank" rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                background: '#ffffff', color: '#000000',
                border: '1px solid #e0e0e0', borderRadius: 5,
                padding: '8px 14px', fontSize: 13 /* = HV2.fsText */, fontWeight: 560,
                textDecoration: 'none', cursor: 'pointer',
              }}>
              {EuFlag}
              Privacy
              {ExternalIcon}
            </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
